import './styles.scss';

import { getClasses, getRangeHandleColor } from '@/utils';
import { useEffect, useRef, useState } from 'react';

import { Badge } from 'react-bootstrap';
import TippyWhen from '../TippyWhen';
import { useRanger } from '@tanstack/react-ranger';

export type RangeSliderProps = {
  min?: number;
  max?: number;
  value?: number[];
  stepSize?: number;
  tickSize?: number;
  disabled?: boolean;
  multiple?: boolean;
  segmentTooltips?: (string | number)[];
  tickLabel?: (value?: number, index?: number) => string | number;
  handleLabel?: (value?: number, index?: number) => string | number;
  handleTooltip?: (value?: number, index?: number) => string | number;
  onChange?: ({ target: { value } }: { target: { value: number[] } }) => any;
  handleColor?: (i: number) => string;
  segmentColor?: (i: number) => string;
};
const RangeSlider = ({
  min = 0,
  max = 100,
  value = [],
  stepSize = 1,
  tickSize = 1,
  disabled = false,
  segmentTooltips = [],
  tickLabel = (val: number): string | number => val,
  handleLabel = (val: number): string | number => '',
  handleTooltip = (val: number): string | number => val,
  handleColor = getRangeHandleColor,
  segmentColor,
  onChange,
}: RangeSliderProps): JSX.Element => {
  const [values, setValues] = useState(value);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const ranger = useRanger<HTMLDivElement>({
    getRangerElement: () => trackRef.current,
    min,
    max,
    stepSize,
    values,
    tickSize,
    onChange: (instance): void => {
      if (disabled) return;
      const nextValues = [...instance.sortedValues];
      setValues(nextValues);
      onChange?.({ target: { value: nextValues } });
    },
  });

  const ticks = ranger.getTicks();
  const segments = ranger.getSteps();
  const handles = ranger.handles();

  useEffect((): void => {
    setValues(value);
  }, [value]);

  return (
    <div className={`RangeSlider${disabled ? ' disabled' : ''}`}>
      <div className="RangeSlider-Track" ref={trackRef} style={{ position: 'relative' }}>
        {ticks.map(({ value, percentage }, index: number): JSX.Element => {
          const label = tickLabel(value, index);
          if (label === '') return null;
          return (
            <div
              className={`RangeSlider-Tick${index === 0 ? ' first' : index === ticks.length - 1 ? ' last' : ''}`}
              style={{ position: 'absolute', left: `${percentage}%` }}
              key={index}
            >
              <div className="RangeSlider-TickLabel">{label}</div>
            </div>
          );
        })}
        {segments.map(({ left, width }, index: number): JSX.Element => {
          return (
            <div
              className={getClasses(
                'RangeSlider-Segment',
                handles?.length && segmentColor ? `bg-${segmentColor(index)}` : undefined,
                handles?.length && !segmentColor && index === 0 ? `bg-${getRangeHandleColor(handles.length - 1)}` : undefined,
                handles?.length && !segmentColor && index !== 0 ? `bg-${getRangeHandleColor(index - 1)}` : undefined,
                !handles?.length ? 'bg-gray-subtle' : undefined
              )}
              style={{ position: 'absolute', left: `${left}%`, width: `${width}%` }}
              key={index}
            >
              {index !== 0 && !!segmentTooltips[index - 1] && (
                <div className={`RangeSlider-Segment-Tooltip${disabled ? ' disabled' : ''}`} key={index}>
                  {segmentTooltips[index - 1]}
                </div>
              )}
            </div>
          );
        })}
        {handles.map(({ value, isActive, onKeyDownHandler, onMouseDownHandler, onTouchStart }, index: number): JSX.Element => {
          const label = handleLabel(value, index);
          const tooltip = handleTooltip(value, index);
          const handleEvents = disabled
            ? {}
            : {
                onKeyDown: onKeyDownHandler,
                onMouseDown: onMouseDownHandler,
                onTouchStart,
              };
          return (
            <button
              type="button"
              aria-label="Range handle"
              disabled={disabled}
              style={{
                appearance: 'none',
                border: 'none',
                background: 'transparent',
                outline: 'none',
                position: 'absolute',
                left: `${ranger.getPercentageForValue(value)}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
              }}
              {...handleEvents}
              key={index}
            >
              <TippyWhen isTrue={label !== ''} options={{ content: tooltip }}>
                <Badge className={`rounded-circle RangeSlider-Handle${isActive ? ' active' : ''}`} bg={handleColor(index)}>
                  {label}
                </Badge>
              </TippyWhen>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default RangeSlider;
